import React, { useState } from 'react';
import { Download, Smartphone, X } from 'lucide-react';
import { Button } from 'components/ui/button';
import { useLanguage } from 'lib/i18n';
import { promptPwaInstall, usePwaInstall } from 'lib/pwaInstall';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from 'components/ui/dialog';

const isIosDevice = () => {
  if (typeof navigator === 'undefined') return false;
  return /iPhone|iPad|iPod/i.test(navigator.userAgent) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
};

export const InstallAppButton = () => {
  const { t } = useLanguage();
  const { isInstalled } = usePwaInstall();
  const [dismissed, setDismissed] = useState(false);
  const [guideOpen, setGuideOpen] = useState(false);

  if (isInstalled || dismissed) return null;

  const handleInstall = async () => {
    if (isIosDevice()) {
      setGuideOpen(true);
      return;
    }

    const prompted = await promptPwaInstall().catch(() => false);
    if (!prompted) setGuideOpen(true);
  };

  return (
    <>
      <aside
        aria-label={t('shell.installBannerTitle')}
        className="fixed bottom-[calc(1rem+env(safe-area-inset-bottom))] left-4 right-4 z-40 rounded-xl border bg-card/95 p-3 shadow-xl backdrop-blur sm:bottom-6 sm:left-auto sm:right-6 sm:w-80"
        data-testid="global-pwa-install-prompt"
      >
        <div className="relative flex items-start gap-3 pr-7">
          <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <Smartphone size={21} aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-sm font-semibold">{t('shell.installBannerTitle')}</p>
            <p className="mt-0.5 text-xs leading-relaxed text-muted-foreground">{t('shell.installBannerDescription')}</p>
          </div>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="absolute -right-2 -top-2 h-8 w-8 text-muted-foreground"
            aria-label={t('shell.dismissInstallPrompt')}
            onClick={() => setDismissed(true)}
            data-testid="dismiss-install-prompt-button"
          >
            <X size={16} aria-hidden="true" />
          </Button>
        </div>
        <Button type="button" className="mt-3 min-h-11 w-full gap-2" onClick={handleInstall} data-testid="install-app-button">
          <Download size={17} aria-hidden="true" />
          {t('shell.installApp')}
        </Button>
      </aside>

      <Dialog open={guideOpen} onOpenChange={setGuideOpen}>
        <DialogContent className="max-h-[calc(100dvh-2rem)] max-w-md overflow-y-auto" data-testid="install-help-guide">
          <DialogHeader>
            <DialogTitle>{t('shell.installGuideTitle')}</DialogTitle>
            <DialogDescription>{t('shell.installGuideIntro')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 text-sm">
            <section className="rounded-lg border p-3" data-testid="install-help-ios">
              <h3 className="font-semibold">{t('shell.installGuideIosTitle')}</h3>
              <ol className="mt-2 list-inside list-decimal space-y-1.5 text-muted-foreground">
                {['shell.installGuideIosShare', 'shell.installGuideIosHome', 'shell.installGuideIosAdd'].map(step => (
                  <li key={step}>{t(step)}</li>
                ))}
              </ol>
            </section>
            <section className="rounded-lg border p-3" data-testid="install-help-android">
              <h3 className="font-semibold">{t('shell.installGuideAndroidTitle')}</h3>
              <p className="mt-1 text-muted-foreground">{t('shell.installGuideAndroidSteps')}</p>
            </section>
            <section className="rounded-lg border p-3" data-testid="install-help-desktop">
              <h3 className="font-semibold">{t('shell.installGuideDesktopTitle')}</h3>
              <p className="mt-1 text-muted-foreground">{t('shell.installGuideDesktopSteps')}</p>
            </section>
            <p className="text-xs text-muted-foreground">{t('shell.installGuideOther')}</p>
          </div>
          <Button type="button" className="min-h-11 w-full" onClick={() => setGuideOpen(false)} data-testid="install-guide-done">
            {t('shell.installGuideDone')}
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};